import { type Client } from 'types/api/attacks/client'
import { type TargetingStrategy } from 'types/api/attacks/details/targeting'
import { type GcpResourceItem } from 'types/api/gcp/integrations'
import { type UserDefinedServiceJiraDefaults } from 'types/api/jira/user-defined-jira-defaults'
import { type K8sClient } from 'types/api/kubernetes/kubernetes'
import { type LoadGenerator } from 'types/api/services/load-generator'
import { type PagedResponse } from 'types/paged-response'
import {
  type ScheduleSettings,
  type ScheduleTriggerType
} from 'types/schedules/schedules'
import { type MultiValueTags, type SingleValueTags } from 'types/tags'

export type ServiceIgnoreReason = 'UNIMPORTANT' | 'UNRECOGNIZED' | 'INCORRECT'

export interface ServiceIgnoredInfo {
  reason: ServiceIgnoreReason
  detail?: string
}

export interface ServiceActiveTargets {
  clients: Client[]
  k8sClients: K8sClient[]
}

export interface SchedulableTest {
  reliabilityTestId: string
  dependencyId?: string
  status: ScheduleTriggerType
}

export interface ServiceOwner {
  identifier: string
}

export enum EvaluationState {
  NoDeploymentFound = 'NO_DEPLOYMENT_FOUND',
  Evaluated = 'EVALUATED',
  RisksNotIncluded = 'RISKS_NOT_INCLUDED' // set when a service's test suite excludes all evaluate-able risks
}

export enum AgentCoverageState {
  Suppressed = 'SUPPRESSED',
  Expired = 'EXPIRED'
}

export interface RiskSummaryStats {
  atRisk: number
  evaluationState: EvaluationState
  previousIntervalAtRisk: number
  total: number
}

export type MultiSelectTags = MultiValueTags

export interface ApplicationSelector {
  applicationName: string
}

export enum AwsResourceType {
  AUTO_SCALING_GROUP = 'AUTO_SCALING_GROUP',
  LOAD_BALANCER = 'LOAD_BALANCER',
  TARGET_GROUP = 'TARGET_GROUP'
}

export interface AwsTarget {
  arn: string
  dnsName?: string | null
  name: string
  region?: string
  resourceType: AwsResourceType
  suggested: boolean
  tags: SingleValueTags
}

export interface AutoScalingGroup extends AwsTarget {
  resourceType: AwsResourceType.AUTO_SCALING_GROUP
}

export interface LoadBalancer extends AwsTarget {
  resourceType: AwsResourceType.LOAD_BALANCER
}

export type GcpTargetResponse = Omit<GcpResourceItem, 'tags' | 'region'> & {
  projectId: string
  region: string | null // null indicates a global resource
}

export type AzureTargetResponse = {
  name: string
  subscriptionId: string
  resourceId: string
  resourceType: 'ApplicationGateway'
}

// TODO: Rename this to Service when we refactor the existing `Service`
type UserDefinedServiceBase = {
  agentCoverageState?: AgentCoverageState | null
  autoCreatingJiraTicketsEnabled: boolean
  awsTargets: AwsTarget[]
  azureTargets: AzureTargetResponse[]
  cachedScore: number
  createdAt: Date | string
  createdFromOnboarding?: boolean
  defaultJiraIssueFields: UserDefinedServiceJiraDefaults
  description?: string | null
  eligibleForAWSIntelligentHealthCheck: boolean
  eligibleForAzureIntelligentHealthCheck: boolean
  eligibleForFFProxyIntelligentHealthCheck: boolean
  eligibleForGCPIntelligentHealthCheck: boolean
  eligibleForGremlinAwsStatusCheck: boolean // this is for any legacy AWS IHC that were created before the new IHC feature to include Azure, GCP, and FF proxy
  enabledGremlinAwsStatusCheck: boolean
  gcpTargets: GcpTargetResponse[]
  intelligentHealthCheckTargetIds?: string[]
  loadGenerators?: LoadGenerator[]
  lastRun?: string
  modifiedDate: string // the date/time of any user-initiated change to the service
  multiSelectTags?: MultiSelectTags
  name: string
  processId?: string
  processName?: string
  processSignature?: string
  riskSummaryStats?: RiskSummaryStats
  schedulableTests: SchedulableTest[]
  scheduleSettings?: ScheduleSettings
  serviceId: string
  serviceOwner?: ServiceOwner
  statusCheckIds?: string[]
  tags: { environment?: string } & SingleValueTags
  teamId: string
  teamOwningProcess?: string
  updatedAt: Date | string // the date/time of any change to the service
}

export type InfraService = UserDefinedServiceBase & {
  applicationSelector?: never
  targetingStrategy: TargetingStrategy
}

export type FailureFlagService = UserDefinedServiceBase & {
  applicationSelector: ApplicationSelector
  targetingStrategy?: never
}

export type UserDefinedService = InfraService | FailureFlagService

export type RunAllTestRequest = {
  tests: string[]
}

export enum ProcessProvenance {
  ProcessData = 'PROCESSDATA',
  Container = 'CONTAINER',
  Kubernetes = 'KUBERNETES'
}

export type PagedUserDefinedServicesResponse = PagedResponse<UserDefinedService>
